/**
 * Expanded day-card body: an optional location tab strip (multi-campus
 * admins) → the day's mealtimes stacked vertically, one order table per
 * mealtime, each headed by its mealtime name — so schools see e.g. breakfast
 * AND lunch at once, and flip between campuses without leaving the day.
 *
 * Three footer modes, mirroring the Parent flow:
 *   - Unordered day → editable inputs held in the per-day DRAFT store;
 *     "Add meals to cart" flushes the draft into the persistent week cart.
 *   - Placed day (summary) → the cells show the ordered quantities as plain
 *     text (green = ordered, muted 0 = not ordered — `placedView` on the
 *     table); an "Edit <Mon D> Order" button swaps them back to inputs.
 *   - Editing a placed day → inputs seeded from the placed order into a local
 *     edit buffer; "Save" resubmits the day (replacing each mealtime's batch in
 *     place, or cancelling a mealtime whose items were all zeroed).
 */
import { includedMilkExcess, includedMilkLimitMessage } from '@ordo/constants/milk-derivation'
import { Alert, AlertDescription, AlertTitle } from '@ordo/ui/alert'
import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { EmptyState } from '@ordo/ui/empty-state'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { ArrowRight, CalendarOff, Pencil, Sparkles } from 'lucide-react'
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'

import { type ComponentKind, useComponentDeliveryRatesQuery, useMilkSharesQuery } from '@/api/location-settings'
import { useLocationActiveMenusQuery } from '@/api/orders'
import {
  type AdminSchool,
  type Mealtime,
  serviceDateIso,
  useCancelSchoolBatchMutation,
  useCheckoutSchoolCartMutation,
  useTypicalQuantitiesQuery,
} from '@/api/school-orders'
import { PriceChangedDialog } from '@/components/orders/price-changed-dialog'
import type { PriceChangeWire } from '@/lib/orders/price-change'
import { withReturnTo } from '@/lib/return-to'
import { orderableMenusFor } from '@/lib/school-orders/active-menus'
import type { AlreadyOrdered } from '@/lib/school-orders/already-ordered'
import { effectiveRatesFor, isDecomposable, splitKinds } from '@/lib/school-orders/breakdown'
import { monthDayLabel } from '@/lib/school-orders/calendar'
import {
  buildCheckoutPlan,
  cartLineKey,
  committedCartLine,
  sameDayLines,
  type SchoolCartLine,
} from '@/lib/school-orders/cart'
import { buildMenuOrderColumns, isItemAllowedForColumn, type OrderColumn } from '@/lib/school-orders/columns'
import {
  capComponentQty,
  capTypedQuantities,
  componentPreviewLabel,
  componentQuantitiesFromBreakdown,
  FULL_RATE_BPS,
  isCustomComponentQuantities,
  kindPartCounts,
  resolveComponentQuantities,
  shouldPersistRateBasis,
} from '@/lib/school-orders/component-quantities'
import { orderClosedReason, schoolDeadlinePolicy } from '@/lib/school-orders/deadline'
import {
  type DerivedMilkMenuItem,
  deriveMilkLines,
  editDerivedMilkKeys,
  groupMilkRowsBySku,
  lineupMilkItems,
  milkDerivationActive,
  milkDerivationColumns,
  milkItemsForColumn,
} from '@/lib/school-orders/derived-milk'
import { reconcileDraftDay } from '@/lib/school-orders/draft-reconcile'
import {
  baggedMilkDerivationGroups,
  baggedMilkShareFor,
  buildExtraRowLines,
  extraFieldTripRows,
  type ExtraOrderRow,
  withBaggedMilkRows,
} from '@/lib/school-orders/field-trip'
import { MEALTIME_LABELS, sortMealtimes } from '@/lib/school-orders/mealtime'
import { apportionQuantity, milkSharePctForColumn } from '@/lib/school-orders/milk'
import {
  buildTypicalPrefillLines,
  narrowComponentKind,
  type PrefillMenuItem,
  shouldPrefillDay,
} from '@/lib/school-orders/prefill'
import {
  collectPriceChanges,
  collectUnpricedMenuItemIds,
  insufficientFundsMessage,
  unpricedFailureMessage,
} from '@/lib/school-orders/price-change'
import { selectLinesFor, useSchoolCart } from '@/stores/school-cart'
import {
  draftLinesForDay,
  draftMealCountForDay,
  selectDraftMapFor,
  useSchoolOrderDraft,
} from '@/stores/school-order-draft'

import { SchoolCampusTabs } from './campus-tabs'
import {
  type ActiveMenuItem,
  type DayOrderDerivedMilk,
  type DayOrderMilk,
  type DayOrderSplit,
  DayOrderTable,
  menuItemImageUri,
  menuItemName,
} from './day-order-table'

export interface DayOrderPanelProps {
  school: AdminSchool
  dateKey: string
  alreadyOrdered: AlreadyOrdered
  /** The placed-orders queries for THIS campus are still loading — suppress
   * states derived from them (prefill bar, editable-vs-placed flip) so
   * switching location tabs doesn't flash the wrong mode (Sheila, 2026-08-24). */
  ordersPending?: boolean
  readOnly?: boolean
  /**
   * Cafeteria "Orders lookup": VIEW-ONLY. Every cell renders as a plain
   * combined (school + parent) number — the caller passes an
   * `AlreadyOrdered` built with `combineFamilyIntoTotals` — with no
   * "+X parent orders" legend, no footers (no cart/Edit), and the closed
   * banner replaced by an "Orders are still being placed" warning while
   * the day's order window is open.
   */
  lookahead?: boolean
  /**
   * All the admin's schools — when more than one, the panel renders a
   * location tab strip (where the mealtime tabs used to be) so admins flip
   * across each campus's orders for the day without leaving the panel.
   */
  schools?: AdminSchool[]
  /** Select another campus from the location tab strip. */
  onSelectSchool?: (locationId: string) => void
}

export function DayOrderPanel({
  school,
  dateKey,
  alreadyOrdered,
  ordersPending = false,
  readOnly = false,
  lookahead = false,
  schools,
  onSelectSchool,
}: DayOrderPanelProps): ReactElement {
  const navigate = useNavigate()
  // Current URL — the billing page's "Back to your order" CTA returns
  // the admin exactly here after adding funds (launch-gap 09).
  const currentLocation = useLocation()
  const returnHerePath = `${currentLocation.pathname}${currentLocation.search}`
  // 402 insufficient_funds off an edit-save: a blocking money condition
  // renders as an inline alert with the "Add funds" CTA, not a toast
  // that vanishes. Cleared on the next save attempt.
  const [fundsError, setFundsError] = useState<string | null>(null)
  const isoDate = serviceDateIso(dateKey)
  // `withAllergens` opts into the per-item dietary tags the order table now
  // renders inline. Must match CollapsedSummary / WeekOrdersView so all three
  // observers share one cache entry (the key includes the flag).
  const { data, isPending } = useLocationActiveMenusQuery({
    locationId: school.id,
    activeOn: isoDate,
    withAllergens: true,
  })
  // The typical-quantities history feeds the meal-selection prefill; gated
  // so opted-out campuses never fetch (hook no-ops on null).
  const typicalQuery = useTypicalQuantitiesQuery(school.prefillMealSelectionEnabled ? school.id : null)

  // A past day, or one whose week deadline has passed, is not orderable — the
  // table renders read-only and the "Add meals to cart" button is disabled.
  // Past dates are always closed, even when the school has no deadline policy.
  const closedReason = orderClosedReason(dateKey, schoolDeadlinePolicy(school))
  const effectiveReadOnly = readOnly || closedReason !== null

  // Mealtimes that actually have a menu on this date, in canonical order.
  const { itemsByMealtime, menuGroupsByMealtime, mealtimes, menuGradeRangeCodeSetsByMealtime } = useMemo(
    () => orderableMenusFor(data),
    [data],
  )

  // Field-trip extra rows: placed batch lines whose menu item is NOT on
  // the browse grid (bagged menus are hidden from the school projection).
  // Without these, a day edit would rebuild the batch from the grid and
  // silently DROP the trip meals — so they render as editable rows and
  // ride along in every rebuilt submission.
  //
  // Plus the bagged menus' system MILK carriers as milk extra rows
  // (PROD-1244): milk is standalone-derived platform-wide, and a bagged
  // menu's milk rows are hidden with the menu — without them a mixed or
  // bagged-only day counted (and paid) no milk for the trip meals.
  const extraRowsByMealtime = useMemo(
    () =>
      withBaggedMilkRows(
        extraFieldTripRows(alreadyOrdered.dayLines(dateKey), itemsByMealtime),
        school.baggedMealtimes,
        dateKey,
      ),
    [alreadyOrdered, dateKey, itemsByMealtime, school.baggedMealtimes],
  )
  // Per mealtime, the bagged meals + carriers the milk derivation runs on
  // (its own group: the bagged menu's SKU policy, not the hot menus').
  const baggedMilkGroups = useMemo(
    () => baggedMilkDerivationGroups({ extraRowsByMealtime, windows: school.baggedMealtimes, dayKey: dateKey }),
    [extraRowsByMealtime, school.baggedMealtimes, dateKey],
  )
  // The stacked sections cover the browse mealtimes PLUS any mealtime that
  // only has placed field-trip lines (e.g. a bagged lunch on a day with no
  // regular lunch menu) so those lines stay visible and editable.
  const displayMealtimes = useMemo(
    () => sortMealtimes([...mealtimes, ...extraRowsByMealtime.keys()]),
    [mealtimes, extraRowsByMealtime],
  )

  // Order columns are PER MEALTIME — the program-keyed locked partition
  // resolved against that mealtime's menus (PK | K-8 | 9-12 lunch-style,
  // PK | K-12 breakfast-style, ONE band-less column on private programs);
  // campus vocabulary columns on every other grouping dimension. A
  // field-trip-only mealtime (no browse menus) gets the full partition —
  // an untagged set — so its placed lines stay editable.
  const columnsByMealtime = useMemo(() => {
    const map = new Map<Mealtime, OrderColumn[]>()
    for (const m of displayMealtimes) {
      const codeSets = menuGradeRangeCodeSetsByMealtime.get(m) ?? (extraRowsByMealtime.has(m) ? [[]] : [])
      map.set(m, buildMenuOrderColumns(school, m, codeSets))
    }
    return map
  }, [school, displayMealtimes, menuGradeRangeCodeSetsByMealtime, extraRowsByMealtime])
  const columnsFor = useCallback((m: Mealtime): OrderColumn[] => columnsByMealtime.get(m) ?? [], [columnsByMealtime])
  // Cross-mealtime UNION — id-keyed lookups and loops that already scope
  // quantities per (mealtime, column) key, where a foreign column simply
  // reads zero. Anything rendering or summarizing uses `columnsFor(m)`.
  const columns = useMemo(() => {
    const seen = new Map<string, OrderColumn>()
    for (const cols of columnsByMealtime.values()) {
      for (const c of cols) {
        if (!seen.has(c.columnId)) {
          seen.set(c.columnId, c)
        }
      }
    }
    return [...seen.values()]
  }, [columnsByMealtime])
  // BAND COLUMN FOLD: a menu-derived band column resolves the per-column rate
  // and milk-share rows the school authored against its CAMPUS grade-range
  // columns — the key space the settings grid writes. Ordered most-preferred
  // first by `bandFoldColumnIds`; see `foldedColumnKeyFor` in lib/.../milk.
  const foldIdsByColumn = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const cols of columnsByMealtime.values()) {
      for (const col of cols) {
        const covered = col.coveredLocationGradeRangeIds ?? []
        if (covered.length > 0 && !map.has(col.columnId)) {
          map.set(col.columnId, covered)
        }
      }
    }
    return map
  }, [columnsByMealtime])
  // Per-column service-style gate: each column carries its EFFECTIVE USDA
  // service style (column override → campus → org, resolved server-side).
  // A mixed campus (straight-serve PK next to OVS K-8) gets component
  // ordering only on its OVS columns.
  const componentColumnIds = useMemo(
    () => new Set(columns.filter((c) => c.serviceStyle === 'OVS').map((c) => c.columnId)),
    [columns],
  )
  const hasComponentColumns = componentColumnIds.size > 0
  // Any mealtime carrying the menus' system milk rows — milk derives on
  // EVERY campus (Straight Serve included), so the rate/share config must
  // load whenever the derivation will run, not only for OVS component
  // ordering.
  const hasSystemMilkRows = useMemo(
    () =>
      baggedMilkGroups.size > 0 ||
      [...itemsByMealtime.values()].some((items) => items.some((it) => it.lineupMilkOptionCode != null)),
    [itemsByMealtime, baggedMilkGroups],
  )
  // Component-split defaults come from the school's per-column rates; gated
  // so campuses with neither component ordering nor system milk never fetch
  // (hooks no-op on null).
  const ratesQuery = useComponentDeliveryRatesQuery(hasComponentColumns || hasSystemMilkRows ? school.id : null)
  const milkSharesQuery = useMilkSharesQuery(hasComponentColumns || hasSystemMilkRows ? school.id : null)

  // Normalized prefill shapes for the history-based seeding.
  const prefillItems = useMemo(() => {
    const map = new Map<Mealtime, PrefillMenuItem[]>()
    for (const m of mealtimes) {
      map.set(
        m,
        (itemsByMealtime.get(m) ?? []).map((it) => ({
          id: it.id,
          itemKind: it.itemKind,
          componentKind: narrowComponentKind(it.component?.kind),
          lineupMilkOptionCode: it.lineupMilkOptionCode ?? null,
          name: menuItemName(it),
          imageUri: menuItemImageUri(it),
          unitPriceCents: it.customerPriceCents,
          currency: it.currency,
          // Column gate inputs — seeding must skip cells the grid dashes
          // out (menu grade band / field-trip scope).
          columnScope: {
            allowedLocationGradeRangeIds: it.allowedLocationGradeRangeIds,
            allowedGroupScopes: it.allowedGroupScopes,
            menuGradeRangeCodes: it.menuGradeRangeCodes,
          },
        })),
      )
    }
    return map
  }, [mealtimes, itemsByMealtime])

  // Per-(mealtime, column) rate lookup: mealtime-scoped rows overwrite the
  // legacy mealtime-NULL rows, and within each tier column-scoped rows
  // overwrite the location-wide fallbacks per kind. Feeds the default
  // component split per order column on each mealtime tab.
  const ratesFor = useMemo(() => {
    const rates = ratesQuery.data?.rates ?? []
    const cache = new Map<string, Partial<Record<ComponentKind, number>>>()
    return (m: Mealtime | null, columnId: string): Partial<Record<ComponentKind, number>> => {
      const key = `${m ?? ''}::${columnId}`
      const cached = cache.get(key)
      if (cached) {
        return cached
      }
      const resolved = effectiveRatesFor(rates, columnId, m, foldIdsByColumn.get(columnId))
      cache.set(key, resolved)
      return resolved
    }
  }, [ratesQuery.data, foldIdsByColumn])

  // Nested milk shares: the school's linked milk SKUs + the default
  // share % per SKU per (mealtime, order column) — configured rows, or an
  // even split when none. Empty SKU list = nothing to nest under BEVERAGE.
  const milkSkus = useMemo(() => milkSharesQuery.data?.milkSkus ?? [], [milkSharesQuery.data])
  const milkShareFor = useMemo(() => {
    const shares = milkSharesQuery.data?.shares ?? []
    const skuIds = milkSkus.map((s) => s.id)
    const cache = new Map<string, Record<string, number>>()
    return (m: Mealtime | null, columnId: string): Record<string, number> => {
      const key = `${m ?? ''}::${columnId}`
      const cached = cache.get(key)
      if (cached) {
        return cached
      }
      const resolved = milkSharePctForColumn(shares, skuIds, columnId, m, foldIdsByColumn.get(columnId))
      cache.set(key, resolved)
      return resolved
    }
  }, [milkSharesQuery.data, milkSkus, foldIdsByColumn])

  // Draft + cart wiring. Inputs are a per-day draft that always REFLECTS the
  // cart: opening a day whose lines are already in the cart seeds the draft
  // from those lines, and "Add meals to cart" / "Update cart" REPLACES the
  // day's cart lines with the draft (idempotent). The draft is deliberately
  // NOT cleared after a commit — blank inputs after an add read as a failed
  // add, and admins re-entering the same numbers is exactly how Larkspur's
  // launch orders doubled (the old commit SUMMED into the cart).
  const draftMap = useSchoolOrderDraft(selectDraftMapFor(school.id))
  const setDraftLine = useSchoolOrderDraft((s) => s.setDraftLine)
  const ensureDaySeeded = useSchoolOrderDraft((s) => s.ensureDaySeeded)
  const seedSuggestedDay = useSchoolOrderDraft((s) => s.seedSuggestedDay)
  const setDayLines = useSchoolCart((s) => s.setDayLines)
  const locationCartLines = useSchoolCart(selectLinesFor(school.id))
  const cartDayLines = useMemo(
    () => locationCartLines.filter((l) => l.date === dateKey && l.quantity > 0),
    [locationCartLines, dateKey],
  )
  const hasCartLinesForDay = cartDayLines.length > 0

  const columnById = useMemo(() => new Map(columns.map((c) => [c.columnId, c])), [columns])

  // A day with any placed quantity is "ordered": its cells show the placed
  // numbers read-only until the user clicks Edit. Editing holds a local buffer
  // (keyed like the cart) seeded from the placed order.
  const dayHasOrder = alreadyOrdered.dayQty(dateKey) > 0
  const placedByNames = alreadyOrdered.dayPlacedByNames(dateKey)
  const [editing, setEditing] = useState(false)
  const [editMap, setEditMap] = useState<Record<string, number>>({})
  // TYPED per-kind component quantities while editing a placed day
  // (cart-line key → kind → count). Seeded from the placed per-kind
  // breakdown in `beginEdit`; absent kinds derive from the meal count.
  const [editComponents, setEditComponents] = useState<Record<string, Record<string, number>>>({})
  // Per-milk-variety counts while editing a placed day (cart-line key →
  // SKU id → count). Placed orders don't round-trip per-SKU quantities, so
  // the buffer starts empty and the editor says they're recalculated on save.
  const [editMilkSkus, setEditMilkSkus] = useState<Record<string, Record<string, number>>>({})
  // Cart-line keys in the edit buffer whose QUANTITY is still system-derived
  // — the edit-mode counterpart of the draft store's `suggested` flag, which
  // is what tracks "this number came from the campus's rates, not a person".
  // Seeded in `beginEdit` from the placed line's `quantitySource`; a key
  // leaves the set the moment someone types over it, and "Reset components"
  // puts it back.
  const [editDerivedKeys, setEditDerivedKeys] = useState<ReadonlySet<string>>(new Set())
  // NOTE: there is no "pending" buffer for component edits. Under the
  // percentage model a % was meaningful before any quantity existed, so
  // edits had to be parked until a meal count arrived. A QUANTITY is not:
  // with zero meals the component cells render a disabled `—`, so there is
  // nothing to park.
  const [saving, setSaving] = useState(false)
  // 409 price_changed on a Save: the drifted lines for the consent
  // dialog + the desired lines of that save (names / new-total rows).
  const [editPriceChanges, setEditPriceChanges] = useState<PriceChangeWire[] | null>(null)
  const [editRepriceLines, setEditRepriceLines] = useState<SchoolCartLine[]>([])
  const checkoutMutation = useCheckoutSchoolCartMutation()
  const cancelMutation = useCancelSchoolBatchMutation()

  // Leaving/opening a different day — or switching campus via the location
  // tabs — drops any in-progress edit (edit-buffer keys don't carry the
  // location, so a buffer must never survive a campus switch).
  useEffect(() => {
    setEditing(false)
    setEditMap({})
    setEditComponents({})
    setEditMilkSkus({})
    setEditPriceChanges(null)
    setEditRepriceLines([])
  }, [dateKey, school.id])

  // Committed-day seeding: opening a day whose lines are already in the
  // cart pre-fills the inputs from those lines, so the sheet always SHOWS
  // what the cart holds. `ensureDaySeeded` never overwrites an existing
  // draft, so in-progress edits win over the committed snapshot.
  useEffect(() => {
    if (dayHasOrder || cartDayLines.length === 0) {
      return
    }
    ensureDaySeeded(school.id, dateKey, cartDayLines)
  }, [dayHasOrder, cartDayLines, ensureDaySeeded, school.id, dateKey])

  // Pre-fill meal selection: seed the day's draft from the school's
  // typical quantities for this weekday the first time an orderable,
  // unordered day is opened. Cells without history stay blank.
  // `seedSuggestedDay` is once-per-day-per-session and never overwrites
  // existing edits, so this effect can safely re-run; the day-card Reset
  // action re-arms it.
  useEffect(() => {
    if (isPending || !typicalQuery.data) {
      return
    }
    if (
      !shouldPrefillDay({
        prefillMealSelectionEnabled: school.prefillMealSelectionEnabled,
        dayHasOrder,
        closed: effectiveReadOnly,
        hasCartLinesForDay,
      })
    ) {
      return
    }
    const lines = buildTypicalPrefillLines({
      locationId: school.id,
      dateKey,
      mealtimes,
      itemsByMealtime: prefillItems,
      columns,
      cells: typicalQuery.data.cells,
    })
    if (lines.length > 0) {
      seedSuggestedDay(school.id, dateKey, lines)
    }
  }, [
    isPending,
    typicalQuery.data,
    school.id,
    school.prefillMealSelectionEnabled,
    dateKey,
    dayHasOrder,
    effectiveReadOnly,
    hasCartLinesForDay,
    mealtimes,
    prefillItems,
    columns,
    seedSuggestedDay,
  ])

  // ---------------------------------------------------------------------
  // Derived milk — the menu's system milk rows (lineupMilkOptionCode) track
  // the meal counts: qty = ceil(meals × BEVERAGE rate) split across SKUs by
  // the milk shares. Suggested lines only; the school's own edits win and
  // the submit path is untouched (milk still ships/pays as its own line).
  // ---------------------------------------------------------------------
  const syncDerivedLines = useSchoolOrderDraft((s) => s.syncDerivedLines)
  const derivedMilkItems = useMemo(() => {
    const map = new Map<Mealtime, DerivedMilkMenuItem[]>()
    for (const m of mealtimes) {
      map.set(
        m,
        (itemsByMealtime.get(m) ?? []).map((it) => ({
          id: it.id,
          itemKind: it.itemKind,
          componentKind: narrowComponentKind(it.component?.kind),
          lineupMilkOptionCode: it.lineupMilkOptionCode ?? null,
          skuComponentId: it.component?.id ?? null,
          mealColumnBucketId: it.mealColumn?.bucketId ?? null,
          name: menuItemName(it),
          imageUri: menuItemImageUri(it),
          unitPriceCents: it.customerPriceCents,
          currency: it.currency,
          columnScope: {
            allowedLocationGradeRangeIds: it.allowedLocationGradeRangeIds,
            allowedGroupScopes: it.allowedGroupScopes,
            menuGradeRangeCodes: it.menuGradeRangeCodes,
          },
        })),
      )
    }
    return map
  }, [mealtimes, itemsByMealtime])
  // Which mealtimes have system milk at all — gates the table's row hiding
  // (`derivedMilkActive`) and the in-breakdown Milk block.
  const derivedMilkMealtimes = useMemo(() => {
    const set = new Set<Mealtime>()
    for (const [m, items] of derivedMilkItems) {
      if (milkDerivationActive(items)) {
        set.add(m)
      }
    }
    return set
  }, [derivedMilkItems])
  useEffect(() => {
    // Draft mode only: placed days edit real quantities, closed days are
    // read-only. EVERY column derives — Straight Serve ones at the default
    // 100% BEVERAGE rate (one carton per meal), matching the server-side
    // DerivedMilkReconciler, which has no service-style gate.
    if (isPending || effectiveReadOnly || dayHasOrder) {
      return
    }
    for (const m of derivedMilkMealtimes) {
      syncDerivedLines(
        school.id,
        deriveMilkLines({
          locationId: school.id,
          dateKey,
          mealtime: m,
          items: derivedMilkItems.get(m) ?? [],
          columns: milkDerivationColumns(columnsFor(m)),
          draftMap,
          ratesFor,
          milkShareFor,
        }),
      )
    }
  }, [
    isPending,
    effectiveReadOnly,
    dayHasOrder,
    columnsFor,
    derivedMilkMealtimes,
    derivedMilkItems,
    school.id,
    dateKey,
    draftMap,
    ratesFor,
    milkShareFor,
    syncDerivedLines,
  ])

  /**
   * Hand every system-milk line of `m` back to the derivation, for every
   * order column. Used by "Reset components"; in draft mode the draft
   * store owns the same transition through its `suggested` flag.
   */
  function resetDerivedMilk(m: Mealtime): void {
    if (!editing) {
      return
    }
    const milkItems = lineupMilkItems(derivedMilkItems.get(m) ?? [])
    if (milkItems.length === 0) {
      return
    }
    setEditDerivedKeys((prev) => {
      const next = new Set(prev)
      for (const item of milkItems) {
        for (const col of milkDerivationColumns(columnsFor(m))) {
          next.add(cartLineKey(dateKey, m, col.columnId, item.id))
        }
      }
      return next
    })
  }

  // Re-derive the system-milk lines held in the EDIT buffer whenever the meal
  // counts or the campus's rates move.
  //
  // The draft-mode sync (`syncDerivedLines` above) cannot serve this: it
  // writes into the DRAFT store, while a placed day being edited keeps its
  // counts in `editMap` — and `deriveMilkLines` used to read meal totals from
  // `draftMap` unconditionally, so pointing it at a placed day derived from
  // zero meals and wrote zeros. It now takes an explicit count source.
  //
  // Only keys still in `editDerivedKeys` are touched, so a number a human
  // typed is never overwritten.
  useEffect(() => {
    if (!editing || editDerivedKeys.size === 0) {
      return
    }
    const next: Record<string, number> = {}
    for (const m of derivedMilkMealtimes) {
      for (const line of deriveMilkLines({
        locationId: school.id,
        dateKey,
        mealtime: m,
        items: derivedMilkItems.get(m) ?? [],
        columns: milkDerivationColumns(columnsFor(m)),
        mealQtyFor: (menuItemId, columnId) => editMap[cartLineKey(dateKey, m, columnId, menuItemId)] ?? 0,
        ratesFor,
        milkShareFor,
      })) {
        if (editDerivedKeys.has(line.key)) {
          next[line.key] = line.quantity
        }
      }
    }
    // Field-trip milk: the bagged meal rows are the meal count and the
    // bagged menu's SKU policy is the split (`baggedMilkShareFor`) — the
    // same math the server ran when it placed the trip.
    for (const [m, group] of baggedMilkGroups) {
      for (const line of deriveMilkLines({
        locationId: school.id,
        dateKey,
        mealtime: m,
        items: group.items,
        columns: milkDerivationColumns(columnsFor(m)),
        mealQtyFor: (menuItemId, columnId) => editMap[cartLineKey(dateKey, m, columnId, menuItemId)] ?? 0,
        ratesFor,
        milkShareFor: baggedMilkShareFor(group, milkShareFor),
      })) {
        if (editDerivedKeys.has(line.key)) {
          next[line.key] = line.quantity
        }
      }
    }
    setEditMap((prev) => {
      let changed = false
      const merged = { ...prev }
      for (const [key, quantity] of Object.entries(next)) {
        if ((prev[key] ?? 0) !== quantity) {
          merged[key] = quantity
          changed = true
        }
      }
      return changed ? merged : prev
    })
  }, [
    editing,
    editDerivedKeys,
    editMap,
    columnsFor,
    dateKey,
    derivedMilkItems,
    derivedMilkMealtimes,
    baggedMilkGroups,
    milkShareFor,
    ratesFor,
    school.id,
  ])

  // Draft keys carry the column + menu-item identities that were RENDERED
  // when the admin typed; the grid re-derives both from live payloads, and
  // either can drift (band cutover ids, duplicate same-label menu items).
  // A stranded line still counts and still commits but reads as an empty
  // cell — typed numbers "vanish" (Hillside, 2026-08-31). Fold stranded
  // typed lines onto the rendered cells when unambiguous; drop stranded
  // suggestions (regenerable); surface whatever remains in the footer.
  const applyDraftReconcile = useSchoolOrderDraft((s) => s.applyDraftReconcile)
  const draftReconcile = useMemo(
    () =>
      reconcileDraftDay({
        lines: draftLinesForDay(draftMap, dateKey),
        grids: displayMealtimes.map((m) => ({
          mealtime: m,
          columns: columnsFor(m),
          items: (itemsByMealtime.get(m) ?? []).map((it) => ({
            id: it.id,
            name: menuItemName(it),
            systemMilk: it.lineupMilkOptionCode != null,
            allowedLocationGradeRangeIds: it.allowedLocationGradeRangeIds,
            allowedGroupScopes: it.allowedGroupScopes,
            menuGradeRangeCodes: it.menuGradeRangeCodes,
          })),
        })),
      }),
    [draftMap, dateKey, displayMealtimes, columnsFor, itemsByMealtime],
  )
  useEffect(() => {
    // Draft mode only, once the grid is real — reconciling against a
    // half-loaded grid would mistake "not fetched yet" for "gone".
    if (isPending || dayHasOrder || editing) {
      return
    }
    if (draftReconcile.moves.length > 0 || draftReconcile.dropKeys.length > 0) {
      applyDraftReconcile(school.id, draftReconcile)
    }
  }, [draftReconcile, applyDraftReconcile, school.id, isPending, dayHasOrder, editing])

  // Meals only — the draft also holds derived system-milk lines (1 per
  // meal at the default beverage rate), which must never read as meals.
  const selectedCount = draftMealCountForDay(draftMap, dateKey)
  // Draft differs from the day's committed cart lines — gates the commit
  // button, so re-committing exactly what the sheet shows is a disabled
  // no-op rather than a second order.
  const dayDirty = !sameDayLines(draftLinesForDay(draftMap, dateKey), cartDayLines)
  // The day-card prefill header (caption + Configure/Reset) shows whenever
  // either Pre-fill setting is on for this school.
  const prefillActive =
    !ordersPending && hasComponentColumns && (school.prefillComponentSplitEnabled || school.prefillMealSelectionEnabled)
  const prefillCaption =
    school.prefillComponentSplitEnabled && school.prefillMealSelectionEnabled
      ? 'Split and quantities pre-filled.'
      : school.prefillComponentSplitEnabled
        ? 'OVS settings pre-filled.'
        : 'Quantities pre-filled from your order history.'

  function cellQtyFor(m: Mealtime, menuItemId: string, columnId: string): number {
    const key = cartLineKey(dateKey, m, columnId, menuItemId)
    if (editing) {
      return editMap[key] ?? 0
    }
    if (dayHasOrder) {
      const column = columnById.get(columnId)
      return column ? alreadyOrdered.cellQty(dateKey, m, column.audienceId, menuItemId) : 0
    }
    return draftMap[key]?.quantity ?? 0
  }

  /** Per-mealtime qty getter for one stacked table. */
  function makeGetQty(m: Mealtime): (menuItemId: string, columnId: string) => number {
    return (menuItemId, columnId) => cellQtyFor(m, menuItemId, columnId)
  }

  /**
   * Committed parent (FAMILY_DIRECT) demand for a cell — the "+X parent
   * orders" legend under each placed-day quantity. Same column-audience
   * mapping as `cellQtyFor`'s placed branch.
   */
  function parentOrderedQtyFor(m: Mealtime, menuItemId: string, columnId: string): number {
    const column = columnById.get(columnId)
    return column ? alreadyOrdered.familyCellQty(dateKey, m, column.audienceId, menuItemId) : 0
  }

  /**
   * Milk-inside-the-meal wiring: the menu's system milk rows are edited in
   * the component breakdown (OVS) or the standalone Milk disclosure
   * (Straight Serve — "non OVS schools should be able to configure milk in
   * dropdown still", Sheila 2026-08-25), writing straight to their own draft
   * lines (the lines stay the ship/pay carrier). Typing a value clears
   * `suggested`, so the derivation stops overwriting that cell.
   *
   * Rows are ONE PER SKU, not one per menu item: a campus running two menus
   * at the same mealtime (a K-8 and a 9-12 lunch) materializes the same milk
   * carton on both, and the school orders "1% milk", not "1% milk on the K-8
   * menu". Each cell resolves to that column's carrier item — for band-scoped
   * menus exactly one — and writes zero the SKU's other carriers so the
   * number on screen is the number that ships.
   */
  function makeDerivedMilk(m: Mealtime): DayOrderDerivedMilk | undefined {
    const skuRows = groupMilkRowsBySku(lineupMilkItems(derivedMilkItems.get(m) ?? []))
    if (skuRows.length === 0) {
      return undefined
    }
    const rowByKey = new Map(skuRows.map((row) => [row.key, row]))
    const itemsFor = (rowKey: string, columnId: string): DerivedMilkMenuItem[] => {
      const row = rowByKey.get(rowKey)
      const column = columns.find((c) => c.columnId === columnId)
      return row && column ? milkItemsForColumn(row, column) : []
    }
    /** Every (SKU carrier × column) cell of this mealtime, with its draft line. */
    const milkCells = (): Array<{ key: string; line: SchoolCartLine | undefined }> =>
      skuRows.flatMap((row) =>
        columnsFor(m).flatMap((col) =>
          milkItemsForColumn(row, col).map((item) => {
            const key = cartLineKey(dateKey, m, col.columnId, item.id)
            return { key, line: draftMap[key] }
          }),
        ),
      )
    return {
      rows: skuRows.map((row) => ({ id: row.key, name: row.name })),
      // A placed day that is not being edited shows what was ORDERED; nothing
      // re-derives until the admin opens Edit. Says so, rather than captioning
      // frozen numbers "auto-filled".
      countsArePlaced: dayHasOrder && !editing,
      isAvailable: (rowKey, columnId) => itemsFor(rowKey, columnId).length > 0,
      // The SKU's quantity in this column is the sum over its carriers — a
      // second carrier is normally 0, but a legacy split draft still reads
      // back as the school's real total.
      getQty: (rowKey, columnId) =>
        itemsFor(rowKey, columnId).reduce((sum, item) => sum + cellQtyFor(m, item.id, columnId), 0),
      isDefault: (rowKey, columnId) =>
        !editing &&
        !dayHasOrder &&
        itemsFor(rowKey, columnId).every(
          (item) => draftMap[cartLineKey(dateKey, m, columnId, item.id)]?.suggested ?? false,
        ),
      onSetQty: (rowKey, columnId, qty) => {
        const column = columnsFor(m).find((c) => c.columnId === columnId)
        const carriers = itemsFor(rowKey, columnId)
        if (!column || carriers.length === 0) {
          return
        }
        // Reuse the ordinary quantity path so edit-mode buffering, draft
        // upserts and line construction all stay in one place.
        carriers.forEach((carrier, index) => {
          const item = (itemsByMealtime.get(m) ?? []).find((i) => i.id === carrier.id)
          if (item) {
            onSetQtyFor(m, item, column, index === 0 ? qty : 0)
          }
        })
      },
      // Standalone (Straight Serve) hosting: "Reset milk" hands every cell
      // back to the share-derived suggestion. OVS keeps its own reset via
      // "Reset components" → resetDerivedMilk.
      hasTyped: () => {
        if (editing) {
          // A milk cell whose edit-buffer value is no longer tracked by the
          // derivation is the school's own number (this also covers legacy
          // placed lines that never carried a DERIVED quantity source).
          return milkCells().some(({ key }) => editMap[key] !== undefined && !editDerivedKeys.has(key))
        }
        if (dayHasOrder) {
          return false // placed summary is read-only; Edit mode owns resets
        }
        return milkCells().some(({ line }) => line !== undefined && !line.suggested)
      },
      onReset: () => {
        if (editing) {
          resetDerivedMilk(m)
          return
        }
        if (dayHasOrder) {
          return
        }
        // Draft mode: deleting the typed lines lets the sync effect re-derive
        // them as `suggested` on the next pass — the same lifecycle a fresh
        // day starts with.
        for (const { line } of milkCells()) {
          if (line && !line.suggested) {
            setDraftLine({ ...line, quantity: 0 })
          }
        }
      },
    }
  }

  /**
   * The campus's rate basis for a bundle in one order column (kind → bps),
   * snapshotted onto the cart line so untouched kinds keep re-deriving
   * without the settings query.
   *
   * The KEY SET is the BUNDLE'S SHAPE and never depends on the pre-fill
   * toggle — only the VALUES do (a campus with pre-fill off ships one of
   * everything per meal, i.e. every kind at 10000 bps). Whether the line
   * sends any opinion at all is a separate decision; see `basisToPersist`.
   */
  function rateBasisFor(m: Mealtime, item: ActiveMenuItem, columnId: string): Record<string, number> {
    const rates = school.prefillComponentSplitEnabled ? ratesFor(m, columnId) : {}
    const basis: Record<string, number> = {}
    for (const kind of splitKinds(item.bundle?.parts ?? [])) {
      basis[kind] = kind === 'MAIN_ENTREE' ? FULL_RATE_BPS : (rates[kind] ?? FULL_RATE_BPS)
    }
    return basis
  }

  /**
   * The rate basis to PERSIST on a line — `{}` meaning "this line has no
   * component opinion", which is what lets the server keep its legacy
   * uniform expansion for a campus that never opted into pre-fill.
   *
   * ⚠️ The moment a line carries ANY typed quantity it must also carry the
   * COMPLETE basis, even with pre-fill off. `overridesFromQuantities` derives
   * the wire map's key set from (basis ∪ typed), and the shipment
   * materializer reads a kind MISSING from a non-null `kindCounts` as **0**
   * (`shipment-materializer.ts:1155`). So an empty basis plus one typed kind
   * would ship `{FRUIT: 50, MAIN_ENTREE: 200}` while the sheet displayed 200
   * vegetables and 200 grains — 400 portions silently dropped, on the DEFAULT
   * campus configuration (`prefillComponentSplitEnabled @default(false)`).
   */
  function basisToPersist(
    m: Mealtime,
    item: ActiveMenuItem,
    columnId: string,
    typed: Record<string, number> | null | undefined,
  ): Record<string, number> {
    const decomposable = item.bundle != null && isDecomposable({ serviceStyle: 'OVS', parts: item.bundle.parts })
    return shouldPersistRateBasis({
      prefillEnabled: school.prefillComponentSplitEnabled,
      decomposable,
      typed,
    })
      ? rateBasisFor(m, item, columnId)
      : {}
  }

  /**
   * Component-quantity wiring for one mealtime's table.
   *
   * Every cell shows a real count. TYPED numbers (draft line / edit buffer /
   * placed breakdown) win; everything else derives from the meal count and
   * the campus's rate — `ceil(meals × bps / 10000)`, the same expression the
   * derived-milk block and the server's `expandShape` use, so the three can
   * never disagree. MAIN_ENTREE is always the meal count.
   */
  function makeSplit(m: Mealtime): DayOrderSplit | undefined {
    if (!hasComponentColumns) {
      return undefined
    }
    // Straight Serve (SS) columns on a mixed campus ship every component
    // whole: cells read the meal count, onSet* ignore them, inputs gray out.
    const isComponentColumn = (columnId: string): boolean => componentColumnIds.has(columnId)
    const itemFor = (menuItemId: string): ActiveMenuItem | undefined =>
      (itemsByMealtime.get(m) ?? []).find((i) => i.id === menuItemId)
    /** The SPARSE typed map — only kinds the school actually entered. */
    const typedFor = (menuItemId: string, columnId: string): Record<string, number> | null => {
      const key = cartLineKey(dateKey, m, columnId, menuItemId)
      if (editing) {
        return editComponents[key] ?? null
      }
      if (dayHasOrder) {
        const column = columnById.get(columnId)
        const placed = column ? alreadyOrdered.cellBreakdown(dateKey, m, column.audienceId, menuItemId) : null
        const item = itemFor(menuItemId)
        // Only kinds a human TYPED come back as typed. Rate-derived kinds
        // are left out so they re-derive from the campus's CURRENT rates —
        // without this a placed day could never pick up a rate change.
        const typedKinds = column ? alreadyOrdered.cellTypedKinds(dateKey, m, column.audienceId, menuItemId) : null
        return placed && item?.bundle
          ? componentQuantitiesFromBreakdown(placed, item.bundle.parts, cellQtyFor(m, menuItemId, columnId), typedKinds)
          : null
      }
      return draftMap[key]?.componentQuantities ?? null
    }
    const resolvedFor = (item: ActiveMenuItem, columnId: string): Record<string, number> =>
      resolveComponentQuantities({
        mealQty: cellQtyFor(m, item.id, columnId),
        typed: typedFor(item.id, columnId),
        rateBps: rateBasisFor(m, item, columnId),
        partCounts: kindPartCounts(item.bundle?.parts ?? []),
      })
    /** Write a typed map back to wherever this day's edits live. */
    const commitTyped = (
      item: ActiveMenuItem,
      column: OrderColumn,
      next: Record<string, number> | null,
      milkNext?: Record<string, number> | null,
    ): void => {
      const key = cartLineKey(dateKey, m, column.columnId, item.id)
      if (editing) {
        setEditComponents((prev) => {
          const copy = { ...prev }
          if (next && Object.keys(next).length > 0) {
            copy[key] = next
          } else {
            delete copy[key]
          }
          return copy
        })
        if (milkNext !== undefined) {
          setEditMilkSkus((prev) => {
            const copy = { ...prev }
            if (milkNext && Object.keys(milkNext).length > 0) {
              copy[key] = milkNext
            } else {
              delete copy[key]
            }
            return copy
          })
        }
        return
      }
      const line = draftMap[key]
      if (!line || line.quantity <= 0) {
        return // zero meals: the cells render a disabled dash, nothing to write
      }
      const { componentQuantities: _q, milkSkuQuantities: _s, ...rest } = line
      setDraftLine({
        ...rest,
        componentRateBps: basisToPersist(m, item, column.columnId, next),
        componentPartCounts: kindPartCounts(item.bundle?.parts ?? []),
        componentTypedKinds: Object.keys(next ?? {}),
        ...(next && Object.keys(next).length > 0 ? { componentQuantities: next } : {}),
        ...(milkNext !== undefined
          ? milkNext && Object.keys(milkNext).length > 0
            ? { milkSkuQuantities: milkNext }
            : {}
          : line.milkSkuQuantities
            ? { milkSkuQuantities: line.milkSkuQuantities }
            : {}),
      })
    }
    // ---- Milk varieties -------------------------------------------------
    // Per-variety COUNTS. The stored map is complete (every linked SKU), not
    // sparse: the BEVERAGE total is defined as their sum, so a partial map
    // would leave the total and the varieties able to disagree — exactly the
    // Σ≠100% state the percentage model had to warn about. Its presence
    // means "the school has taken control of milk varieties"; "Reset
    // components" hands control back.
    const milkTypedFor = (menuItemId: string, columnId: string): Record<string, number> | null => {
      const key = cartLineKey(dateKey, m, columnId, menuItemId)
      if (editing) {
        return editMilkSkus[key] ?? null
      }
      if (dayHasOrder) {
        return null // placed per-SKU counts have no OrderItem carrier
      }
      return draftMap[key]?.milkSkuQuantities ?? null
    }
    const milkQuantitiesFor = (item: ActiveMenuItem, columnId: string): Record<string, number> => {
      const typed = milkTypedFor(item.id, columnId)
      if (typed) {
        // Stored raw; capped at the meal count at read (same rule as
        // resolveComponentQuantities — the buffer must survive transient
        // keystroke meal counts).
        return capTypedQuantities(typed, cellQtyFor(m, item.id, columnId)) ?? {}
      }
      const beverageQty = resolvedFor(item, columnId)['BEVERAGE'] ?? 0
      return apportionQuantity(beverageQty, milkShareFor(m, columnId))
    }
    const milk: DayOrderMilk | undefined =
      milkSkus.length > 0
        ? {
            skus: milkSkus,
            getQty: (menuItemId, columnId, componentId) => {
              const item = itemFor(menuItemId)
              return item ? (milkQuantitiesFor(item, columnId)[componentId] ?? 0) : 0
            },
            isDefault: (menuItemId, columnId) => milkTypedFor(menuItemId, columnId) == null,
            onSetQty: (item, column, componentId, qty) => {
              if (!isComponentColumn(column.columnId)) {
                return // Straight Serve column: no per-variety milk ordering
              }
              const mealQty = cellQtyFor(m, item.id, column.columnId)
              const milkNext = {
                ...milkQuantitiesFor(item, column.columnId),
                [componentId]: capComponentQty(qty, mealQty),
              }
              // The BEVERAGE total IS the sum of the varieties — pin it so
              // the number on screen is the number on the wire.
              const total = Object.values(milkNext).reduce((sum, n) => sum + n, 0)
              commitTyped(item, column, { ...(typedFor(item.id, column.columnId) ?? {}), BEVERAGE: total }, milkNext)
            },
            onClearQty: (item, column, componentId) => {
              if (!isComponentColumn(column.columnId)) {
                return
              }
              const milkNext = { ...milkQuantitiesFor(item, column.columnId) }
              delete milkNext[componentId]
              const typed = { ...(typedFor(item.id, column.columnId) ?? {}) }
              if (Object.keys(milkNext).length === 0) {
                delete typed['BEVERAGE']
                commitTyped(item, column, typed, null)
                return
              }
              typed['BEVERAGE'] = Object.values(milkNext).reduce((sum, n) => sum + n, 0)
              commitTyped(item, column, typed, milkNext)
            },
            recalculatedOnSave: dayHasOrder,
          }
        : undefined
    return {
      supports: (item) => item.bundle != null && isDecomposable({ serviceStyle: 'OVS', parts: item.bundle.parts }),
      isComponentColumn,
      getQty: (menuItemId, columnId, kind) => {
        const mealQty = cellQtyFor(m, menuItemId, columnId)
        if (!isComponentColumn(columnId)) {
          return mealQty // Straight Serve column: every component ships whole
        }
        const item = itemFor(menuItemId)
        return item ? (resolvedFor(item, columnId)[kind] ?? mealQty) : mealQty
      },
      onSetQty: (item, column, kind, qty) => {
        if (kind === 'MAIN_ENTREE' || !isComponentColumn(column.columnId)) {
          return // one main per meal ordered; Straight Serve columns are locked
        }
        const mealQty = cellQtyFor(m, item.id, column.columnId)
        // A kind in N bundle parts serves up to N per meal — cap at N × meals.
        const partCount = item.bundle ? kindPartCounts(item.bundle.parts)[kind] : 1
        commitTyped(item, column, {
          ...(typedFor(item.id, column.columnId) ?? {}),
          [kind]: capComponentQty(qty, mealQty, partCount ?? 1),
        })
      },
      onClearQty: (item, column, kind) => {
        if (kind === 'MAIN_ENTREE' || !isComponentColumn(column.columnId)) {
          return
        }
        const next = { ...(typedFor(item.id, column.columnId) ?? {}) }
        delete next[kind]
        commitTyped(item, column, next)
      },
      isCustom: (menuItemId) => {
        const item = itemFor(menuItemId)
        if (!item) {
          return false
        }
        return columnsFor(m).some(
          (col) =>
            isComponentColumn(col.columnId) &&
            isCustomComponentQuantities(resolvedFor(item, col.columnId), cellQtyFor(m, item.id, col.columnId)),
        )
      },
      // Per-KIND, so typing a fruit number doesn't flip milk to "edited".
      isDefault: (menuItemId, columnId, kind) =>
        isComponentColumn(columnId) && !(kind in (typedFor(menuItemId, columnId) ?? {})),
      summaryFor: (item) => {
        const bundleKinds = splitKinds(item.bundle?.parts ?? [])
        // Milk is a BEVERAGE part on some menus (a genuinely per-bundle
        // number, so the preview states it) and its own system row on
        // others. System milk is a MEALTIME aggregate stated once by the
        // Milk disclosure — repeating it on every bundle's preview read as
        // additive (PROD-1214), so it stays out of the label here.
        const milkInBundle = bundleKinds.includes('BEVERAGE')
        return componentPreviewLabel(
          bundleKinds,
          columnsFor(m).map((col) => ({
            label: col.label,
            mealQty: cellQtyFor(m, item.id, col.columnId),
            quantities: isComponentColumn(col.columnId) ? resolvedFor(item, col.columnId) : {},
          })),
          { milkVarietyCount: milkInBundle ? milkSkus.length : 0 },
        )
      },
      hasTyped: (menuItemId) =>
        columnsFor(m).some((col) => {
          const typed = typedFor(menuItemId, col.columnId)
          return typed != null && Object.keys(typed).length > 0
        }),
      onResetRow: (item) => {
        for (const col of columnsFor(m)) {
          if (isComponentColumn(col.columnId)) {
            commitTyped(item, col, null, null)
          }
        }
        // System MILK rows deliberately stay out of this reset. While milk
        // rendered INSIDE the breakdown, "Reset components" had to hand the
        // milk keys back to the derivation too (Larkspur, 2026-08-19 — a
        // reset appeared to do nothing after a BEVERAGE rate change). Since
        // PROD-1214 hoisted milk to the mealtime-level disclosure, that
        // surface owns its own "Reset milk" (`derivedMilk.onReset`, which
        // calls `resetDerivedMilk` in edit mode) — resetting it from a
        // bundle row would be an invisible side effect.
      },
      ...(milk ? { milk } : {}),
    }
  }

  /** Per-mealtime qty setter for one stacked table. */
  function makeOnSetQty(m: Mealtime): (item: ActiveMenuItem, column: OrderColumn, qty: number) => void {
    return (item, column, qty) => onSetQtyFor(m, item, column, qty)
  }

  /** `onSetQty` for an EXPLICIT mealtime (the milk block edits its own). */
  function onSetQtyFor(m: Mealtime, item: ActiveMenuItem, column: OrderColumn, qty: number): void {
    const mealtime = m
    const key = cartLineKey(dateKey, mealtime, column.columnId, item.id)
    if (editing) {
      // The buffers keep their RAW typed values — never write them back
      // capped. The quantity input commits per keystroke, so typing "180"
      // passes through 1 and 18; capping the buffer at those transients
      // ratcheted {FRUIT: 40} down to 1 permanently (Hall MS, week of
      // 8/24). Displayed == shipped still holds: `resolveComponentQuantities`
      // caps typed values at the meal count at READ time, and
      // `handleSaveEdit` caps again at the final quantity on the way out.
      setEditMap((prev) => ({ ...prev, [key]: qty }))
      // A hand-entered number is no longer a derivation — stop re-deriving
      // this line for the rest of the edit (mirrors the draft store, where
      // the sync never overwrites a non-suggested line).
      //
      // NB: this deliberately does NOT re-cap `editComponents` /
      // `editMilkSkus` here. An earlier version of this branch did, but the
      // keystroke-ratchet fix on main removed exactly that: the quantity
      // input commits per keystroke, so capping the buffer at each in-flight
      // value ratcheted {FRUIT: 40} down to 1 while typing "180". Capping
      // now happens at READ time and again in `handleSaveEdit`.
      setEditDerivedKeys((prev) => {
        if (!prev.has(key)) {
          return prev
        }
        const next = new Set(prev)
        next.delete(key)
        return next
      })
      return
    }
    // Same on the draft path: the stored typed numbers survive a meal-count
    // edit RAW; the cap to the meal count happens at resolve time (display)
    // and in `overridesFromQuantities` (wire), so a school that typed 135
    // fruit against 200 meals and then drops to 120 ships 120 — and gets
    // its 135 back if the meal count comes back up.
    const prior = draftMap[key]
    const componentQuantities = prior?.componentQuantities ?? null
    const milkSkuQuantities = prior?.milkSkuQuantities ?? null
    const componentRateBps = basisToPersist(mealtime, item, column.columnId, componentQuantities)
    const line: SchoolCartLine = {
      key,
      locationId: school.id,
      date: dateKey,
      mealtime,
      columnId: column.columnId,
      gradeRangeId: column.gradeRangeId,
      groupDimension: column.groupDimension,
      groupValue: column.groupValue,
      menuItemId: item.id,
      name: menuItemName(item),
      imageUri: menuItemImageUri(item),
      unitPriceCents: item.customerPriceCents,
      currency: item.currency,
      quantity: qty,
      componentPartCounts: kindPartCounts(item.bundle?.parts ?? []),
      ...(Object.keys(componentRateBps).length > 0 ? { componentRateBps } : {}),
      ...(componentQuantities ? { componentQuantities } : {}),
      ...(milkSkuQuantities ? { milkSkuQuantities } : {}),
      // PROVENANCE — which kinds are the school's own numbers.
      componentTypedKinds: Object.keys(componentQuantities ?? {}),
      // System-milk identity: the milk block's edits route through here
      // (`makeDerivedMilk.onSetQty`), and a hand-edited milk line must
      // keep being excluded from the "N meals" counters.
      ...(item.lineupMilkOptionCode != null ? { systemMilk: true } : {}),
    }
    setDraftLine(line)
  }

  // Field-trip extra rows only exist on placed days, so their quantity
  // edits always land in the placed-day edit buffer.
  function makeOnSetExtraQty(m: Mealtime): (row: ExtraOrderRow, column: OrderColumn, qty: number) => void {
    return (row, column, qty) => {
      if (!editing) {
        return
      }
      const key = cartLineKey(dateKey, m, column.columnId, row.menuItemId)
      setEditMap((prev) => ({ ...prev, [key]: qty }))
      // A hand-entered milk count is no longer a derivation (same rule as
      // `onSetQtyFor`): stop re-deriving this cell for the rest of the edit.
      setEditDerivedKeys((prev) => {
        if (!prev.has(key)) {
          return prev
        }
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  const milkLimitErrors = mealtimes.flatMap((m) => {
    const items = new Map((derivedMilkItems.get(m) ?? []).map((item) => [item.id, item]))
    for (const item of baggedMilkGroups.get(m)?.items ?? []) {
      items.set(item.id, item)
    }
    return columnsFor(m).flatMap((column) => {
      const excess = includedMilkExcess(
        [...items.values()].map((item) => ({
          scope: column.columnId,
          quantity: cellQtyFor(m, item.id, column.columnId),
          systemMilk: item.lineupMilkOptionCode != null,
          bundle: item.itemKind === 'BUNDLE',
          bucket: item.mealColumnBucketId,
        })),
      )
      return excess.map((entry) => `${m} · ${column.label}: ${includedMilkLimitMessage(entry)}`)
    })
  })

  function handleAddToCart() {
    if (milkLimitErrors.length > 0) {
      return
    }
    // Suggestions become real order lines on commit; the cart never carries
    // the `suggested` flag. Each decomposable line carries its rate BASIS,
    // so untouched components keep deriving the same counts the sheet
    // showed — displayed is shipped, with no re-resolution at checkout.
    //
    // The commit REPLACES the day's cart lines with the draft (never sums —
    // committing what the sheet shows twice must be a no-op), and the draft
    // stays put so the inputs keep showing what's now in the cart.
    const itemById = new Map<string, ActiveMenuItem>()
    for (const m of mealtimes) {
      for (const it of itemsByMealtime.get(m) ?? []) {
        itemById.set(it.id, it)
      }
    }
    setDayLines(
      school.id,
      dateKey,
      draftLinesForDay(draftMap, dateKey).map((draftLine) => {
        // Drops `suggested` (draft state), but KEEPS it for a still-derived
        // system-milk line — the only carrier of "this count still tracks the
        // meal counts". See `committedCartLine`: stripping it unconditionally
        // meant no school-placed milk line was ever stored `DERIVED`, so a
        // later edit could never re-derive it and milk froze at the meal count
        // the day was first committed with.
        const line = committedCartLine(draftLine)
        // Gated per LINE by its own order column: straight-serve columns
        // ship every component whole, even on a campus whose other columns
        // order component-wise.
        if (!componentColumnIds.has(line.columnId)) {
          const {
            componentQuantities: _q,
            componentRateBps: _r,
            componentPartCounts: _p,
            milkSkuQuantities: _s,
            ...mealLine
          } = line
          return mealLine
        }
        const item = itemById.get(line.menuItemId)
        if (!item) {
          return line
        }
        // `basisToPersist` returns {} for a line with no component opinion
        // (pre-fill off and nothing typed), which keeps the server's legacy
        // uniform expansion. A line WITH typed counts always gets the
        // complete basis, so the wire map can never be sparse.
        const componentRateBps =
          line.componentRateBps ?? basisToPersist(line.mealtime, item, line.columnId, line.componentQuantities)
        return {
          ...line,
          componentPartCounts: kindPartCounts(item.bundle?.parts ?? []),
          ...(Object.keys(componentRateBps).length > 0 ? { componentRateBps } : {}),
        }
      }),
    )
  }

  // Seed the edit buffer from the placed order across every mealtime, so
  // switching tabs mid-edit preserves values.
  function beginEdit() {
    const seed: Record<string, number> = {}
    const componentSeed: Record<string, Record<string, number>> = {}
    // Field-trip extra rows seed like normal rows — their placed
    // quantities land in the edit buffer keyed by column, so a save
    // resubmits them (and a zeroed one cancels through the same path).
    for (const [m, rows] of extraRowsByMealtime) {
      for (const row of rows) {
        for (const col of columnsFor(m)) {
          const q = alreadyOrdered.cellQty(dateKey, m, col.audienceId, row.menuItemId)
          if (q > 0) {
            seed[cartLineKey(dateKey, m, col.columnId, row.menuItemId)] = q
          }
        }
      }
    }
    for (const m of mealtimes) {
      for (const it of itemsByMealtime.get(m) ?? []) {
        for (const col of columnsFor(m)) {
          // Gated cells render a dash, so they can't hold an editable
          // quantity — a placed qty from before the item's menu/scope was
          // gated stays out of the buffer (saving converges the order to
          // what the sheet shows).
          if (!isItemAllowedForColumn(it, col)) {
            continue
          }
          const q = alreadyOrdered.cellQty(dateKey, m, col.audienceId, it.id)
          if (q > 0) {
            const key = cartLineKey(dateKey, m, col.columnId, it.id)
            seed[key] = q
            // Placed per-kind quantities round-trip into the edit buffer
            // AS QUANTITIES — lossless, unlike the old percent hop, which
            // could redisplay an order of 135 as 136.
            const placed = alreadyOrdered.cellBreakdown(dateKey, m, col.audienceId, it.id)
            // Seed ONLY what a human typed. Before provenance existed this
            // seeded the COMPLETE map, which marked every kind as typed and
            // is why an edit could never re-derive against changed rates.
            const typedKinds = alreadyOrdered.cellTypedKinds(dateKey, m, col.audienceId, it.id)
            const placedQuantities = it.bundle
              ? componentQuantitiesFromBreakdown(placed, it.bundle.parts, q, typedKinds)
              : null
            if (placedQuantities) {
              componentSeed[key] = placedQuantities
            }
          }
        }
      }
    }
    setEditMap(seed)
    setEditComponents(componentSeed)
    // The milk cells that keep deriving in Edit — the hot menus' system milk
    // rows and the bagged carriers under ONE rule (`editDerivedMilkKeys`): a
    // placed DERIVED line, or a cell the day never placed. Seeding only the
    // placed DERIVED lines here left every hot-menu milk cell frozen at 0 on
    // a day the field-trip placer had created first (Amana, 2026-09-14/16).
    setEditDerivedKeys(
      editDerivedMilkKeys({
        dateKey,
        itemsByMealtime: derivedMilkItems,
        extraRowsByMealtime,
        columnsFor,
        placed: {
          qty: (m, audienceId, menuItemId) => alreadyOrdered.cellQty(dateKey, m, audienceId, menuItemId),
          isDerived: (m, audienceId, menuItemId) => alreadyOrdered.cellQtyIsDerived(dateKey, m, audienceId, menuItemId),
        },
      }),
    )
    setEditing(true)
  }

  function cancelEdit() {
    setEditing(false)
    setEditMap({})
    setEditComponents({})
    setEditDerivedKeys(new Set())
    setEditMilkSkus({})
  }

  async function handleSaveEdit(acceptRepricing = false) {
    if (milkLimitErrors.length > 0) {
      return
    }
    // Build the full desired order from the edit buffer (a submit REPLACES the
    // mealtime's batch, so we send the complete set, not deltas).
    const lines: SchoolCartLine[] = []
    for (const m of mealtimes) {
      for (const it of itemsByMealtime.get(m) ?? []) {
        for (const col of columnsFor(m)) {
          // Mirror the grid's column hard-gate: never resubmit a line the
          // sheet dashes out (the server rejects such lines outright).
          if (!isItemAllowedForColumn(it, col)) {
            continue
          }
          const key = cartLineKey(dateKey, m, col.columnId, it.id)
          const quantity = editMap[key] ?? 0
          if (quantity <= 0) {
            continue
          }
          // Straight Serve columns resubmit whole meals — no component
          // quantities or per-variety milk counts, even if a pre-override
          // placed breakdown seeded the edit buffer.
          const columnByComponent = componentColumnIds.has(col.columnId)
          // Typed component counts are re-capped to the (possibly edited)
          // meal quantity, exactly as the draft path does.
          const componentPartCounts = kindPartCounts(it.bundle?.parts ?? [])
          const componentQuantities = columnByComponent
            ? capTypedQuantities(editComponents[key], quantity, componentPartCounts)
            : null
          const milkSkuQuantities = columnByComponent ? capTypedQuantities(editMilkSkus[key], quantity) : null
          const componentRateBps = columnByComponent ? basisToPersist(m, it, col.columnId, componentQuantities) : {}
          lines.push({
            key,
            locationId: school.id,
            date: dateKey,
            mealtime: m,
            columnId: col.columnId,
            gradeRangeId: col.gradeRangeId,
            groupDimension: col.groupDimension,
            groupValue: col.groupValue,
            menuItemId: it.id,
            name: menuItemName(it),
            imageUri: menuItemImageUri(it),
            unitPriceCents: it.customerPriceCents,
            currency: it.currency,
            quantity,
            // The edit buffer's counts ride along unchanged; absent =
            // derive from the rate basis / server default.
            ...(columnByComponent ? { componentPartCounts } : {}),
            ...(Object.keys(componentRateBps).length > 0 ? { componentRateBps } : {}),
            ...(componentQuantities ? { componentQuantities } : {}),
            ...(milkSkuQuantities ? { milkSkuQuantities } : {}),
            // PROVENANCE: exactly the kinds a human typed. The wire map is
            // complete, so this is the only thing that keeps the NEXT
            // pre-deadline edit able to re-derive the rest.
            ...(columnByComponent ? { componentTypedKinds: Object.keys(componentQuantities ?? {}) } : {}),
            // System-milk identity — the wire stamps quantitySource DERIVED
            // off `systemMilk && suggested`, so without this the next flag
            // alone never round-trips and every edit-save froze milk as
            // typed.
            ...(it.lineupMilkOptionCode != null ? { systemMilk: true } : {}),
            // A line still tracking the derivation stays DERIVED on the
            // order, so a later rate change reaches it too.
            ...(editDerivedKeys.has(key) ? { suggested: true } : {}),
          })
        }
      }
    }
    // Field-trip extra rows ride along in the rebuilt set — a replace
    // submits the COMPLETE order, so omitting them would drop the trip
    // meals from the batch.
    lines.push(
      ...buildExtraRowLines({
        locationId: school.id,
        dateKey,
        columns,
        extraRowsByMealtime,
        qtyFor: (m, menuItemId, columnId) => editMap[cartLineKey(dateKey, m, columnId, menuItemId)] ?? 0,
        // Milk rows still tracking the derivation stay DERIVED on the order.
        isDerived: (key) => editDerivedKeys.has(key),
      }),
    )
    const groups = buildCheckoutPlan(lines)
    const withLines = new Set(groups.map((g) => g.mealtime))
    // A mealtime that was placed but is now all-zero is a full removal → cancel
    // its batch (submit rejects an empty cart). Field-trip-only mealtimes
    // (in `displayMealtimes` but not the browse list) cancel the same way.
    const cancelBatchIds = displayMealtimes
      .filter((m) => !withLines.has(m))
      .map((m) => alreadyOrdered.batchId(dateKey, m))
      .filter((id): id is string => id !== null)

    setSaving(true)
    setFundsError(null)
    try {
      if (groups.length > 0) {
        const result = await checkoutMutation.mutateAsync({ school, groups, acceptRepricing })
        if (!result.allOk) {
          const failed = result.results.filter((r) => !r.ok)
          // Prices drifted since the order was placed/shown (409
          // price_changed) — nothing was replaced for those mealtimes.
          // Open the consent dialog; confirming re-runs this save with
          // acceptRepricing: true (the edit buffer is still intact).
          const changes = collectPriceChanges(failed)
          if (changes.length > 0) {
            setEditRepriceLines(lines)
            setEditPriceChanges(changes)
            return
          }
          const unpricedIds = collectUnpricedMenuItemIds(failed)
          if (unpricedIds.length > 0) {
            toast.error('Could not update order', { description: unpricedFailureMessage(unpricedIds, lines) })
            return
          }
          const noFunds = failed.find((f) => f.insufficientFunds)
          if (noFunds) {
            // Action-first inline alert (launch-gap 09) — a blocking
            // money condition shouldn't live in a toast that vanishes.
            setFundsError(noFunds.error ?? insufficientFundsMessage(null))
            return
          }
          throw new Error(failed[0]?.error ?? 'Could not update order')
        }
      }
      for (const batchId of cancelBatchIds) {
        await cancelMutation.mutateAsync(batchId)
      }
      toast.success(groups.length === 0 ? 'Order removed' : 'Order updated')
      setEditing(false)
      setEditMap({})
      setEditComponents({})
      setEditMilkSkus({})
      setEditPriceChanges(null)
      setEditRepriceLines([])
    } catch (error) {
      toast.error('Could not update order', {
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setSaving(false)
    }
  }

  // The table is uneditable both when ordering is closed and when a placed day
  // is being viewed (not yet in Edit mode). Lookahead is always uneditable.
  const tableReadOnly = effectiveReadOnly || lookahead || ordersPending || (dayHasOrder && !editing)

  if (isPending) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }
  // Location tab strip (multi-campus admins only) — sits where the mealtime
  // tabs used to, so schools flip across each campus's orders for the day.
  // The strip must SCROLL when a district has more campuses than fit —
  // TabsList clips overflow, so campuses past the edge were unreachable
  // (OLHSA, 7 campuses; Sheila, 2026-08-24). w-max inside an overflow-x
  // wrapper keeps every trigger reachable; min-w-full preserves the
  // full-width underline when the strip is short.
  const locationTabs =
    schools && schools.length > 1 && onSelectSchool ? (
      <SchoolCampusTabs schools={schools} value={school.id} onValueChange={onSelectSchool} />
    ) : null

  if (displayMealtimes.length === 0) {
    // The location strip stays visible so an admin can hop to a campus that
    // does have a menu this day.
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {locationTabs}
        <EmptyState
          icon={<CalendarOff className="h-10 w-10" />}
          title="No menu this day"
          description="There's no active menu at this school on this date."
          padding="sm"
        />
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {lookahead ? (
        // Lookahead inverts the banner: a CLOSED day is final (the normal,
        // quiet state for a prep view); an OPEN window means the numbers on
        // screen can still grow — warn the worker.
        closedReason === null ? (
          <p className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-400">
            Orders are still being placed for this date.
          </p>
        ) : null
      ) : closedReason ? (
        <p className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-400">
          {closedReason === 'PAST'
            ? 'This date has passed — ordering is closed.'
            : 'The order deadline for this week has passed — ordering is closed.'}
        </p>
      ) : null}
      {(!dayHasOrder || editing) && milkLimitErrors.length > 0 ? (
        <Alert variant="error">
          <AlertTitle>Check included milk quantities</AlertTitle>
          <AlertDescription>
            {milkLimitErrors.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </AlertDescription>
        </Alert>
      ) : null}
      {fundsError ? (
        <div className="shrink-0 border-b p-3">
          <Alert variant="error">
            <AlertTitle>Add funds to place this order</AlertTitle>
            <AlertDescription>
              <p>{fundsError} Your changes are kept here — add funds, then save again.</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" onClick={() => void navigate(withReturnTo('/billing/school', returnHerePath))}>
                  Add funds
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setFundsError(null)}>
                  Dismiss
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      ) : null}
      {locationTabs}
      {prefillActive && !tableReadOnly ? (
        <div className="flex shrink-0 items-center justify-between gap-3 border-b bg-muted/40 px-4 py-2">
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2} />
            {prefillCaption}
          </p>
          {/* One clear action. The old Reset button silently wiped the day's
              typed quantities and read as "clear my cart" — removed (Sheila,
              2026-08-24); prefill settings changes live behind Update. */}
          <Button variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-xs font-medium text-primary" asChild>
            <Link to="/settings/my-school">
              Update
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      ) : null}
      {/* Mealtimes stack vertically — every mealtime's table is visible at
          once, each headed by its mealtime name in the first column. One
          shared scroll region so the day reads as a single sheet. */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {displayMealtimes.map((m, i) => (
          <div key={m} className={cn(i > 0 && 'border-t')}>
            <DayOrderTable
              items={itemsByMealtime.get(m) ?? []}
              serviceDate={isoDate}
              groups={menuGroupsByMealtime.get(m)}
              columns={columnsFor(m)}
              mealtimeLabel={MEALTIME_LABELS[m]}
              getQty={makeGetQty(m)}
              onSetQty={makeOnSetQty(m)}
              readOnly={tableReadOnly}
              // Placed-day summary: quantities read as plain numbers (green =
              // ordered, muted 0 = not ordered) instead of disabled inputs;
              // Edit swaps the cells back to inputs. Lookahead is ALWAYS the
              // plain-number summary (combined totals, no legend).
              placedView={lookahead || (dayHasOrder && !editing)}
              parentOrderedQty={
                lookahead ? undefined : (menuItemId, columnId) => parentOrderedQtyFor(m, menuItemId, columnId)
              }
              isSuggested={(menuItemId, columnId) =>
                !editing &&
                !dayHasOrder &&
                (draftMap[cartLineKey(dateKey, m, columnId, menuItemId)]?.suggested ?? false)
              }
              showItemPrices={school.showItemPricesOnOrderTable}
              derivedMilkActive={derivedMilkMealtimes.has(m)}
              derivedMilk={makeDerivedMilk(m)}
              holidayTreats={(data?.holidayTreats ?? []).filter((treat) => treat.mealtime === m)}
              split={makeSplit(m)}
              extraRows={extraRowsByMealtime.get(m)}
              onSetExtraQty={makeOnSetExtraQty(m)}
              // Chef pre-approved swap (order-local): show the replacement
              // the kitchen is actually serving, not the pre-swap menu name.
              substitutedNameFor={(menuItemId) => alreadyOrdered.substitutedName(dateKey, m, menuItemId)}
            />
          </div>
        ))}
      </div>

      {lookahead ? null : editing ? (
        <div className="flex shrink-0 items-center justify-end gap-2 border-t px-4 py-4">
          <Button variant="outline" onClick={cancelEdit} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSaveEdit()} disabled={saving || milkLimitErrors.length > 0}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      ) : dayHasOrder ? (
        <div className="flex shrink-0 items-center justify-between gap-3 border-t px-4 py-4">
          {/* School orders are account-owned — any admin edits the same
              order, so name the placer to make the shared model legible. */}
          <span className="min-w-0 truncate text-sm font-medium text-muted-foreground">
            {placedByNames.length > 0 ? `Order placed by ${placedByNames.join(', ')}` : 'Order placed'}
          </span>
          <Button onClick={beginEdit} disabled={effectiveReadOnly}>
            <Pencil aria-hidden />
            Edit {monthDayLabel(dateKey)} Order
          </Button>
        </div>
      ) : readOnly ? null : (
        // A read-only surface (org SCHOOL_ORDER channel off, or a PARENT-only
        // campus) never drafts, so "0 meals selected" beside a dead "Add meals
        // to cart" only advertised an order it cannot place (FACTORY-554). A
        // closed day on an orderable campus keeps this footer.
        <div className="flex shrink-0 items-center justify-between border-t px-4 py-4">
          {/* Once the day is committed and unchanged, say "in cart" — blank
              feedback after an add is how launch admins re-entered their
              quantities and doubled their orders. */}
          <span className="min-w-0 text-sm font-medium text-foreground">
            {selectedCount} {selectedCount === 1 ? 'meal' : 'meals'}{' '}
            {hasCartLinesForDay && !dayDirty ? 'in cart' : 'selected'}
            {draftReconcile.unmatchedMealQty > 0 ? (
              // Stranded typed lines the reconciler could not place (the
              // menu changed under them) — named, so the count can never
              // silently disagree with the visible cells.
              <span className="block text-xs font-normal text-muted-foreground">
                {draftReconcile.unmatchedMealQty} of these were picked on an earlier menu view. Reset the day to clear
                them.
              </span>
            ) : null}
          </span>
          <Button onClick={handleAddToCart} disabled={effectiveReadOnly || !dayDirty || milkLimitErrors.length > 0}>
            {hasCartLinesForDay ? 'Update cart' : 'Add meals to cart'}
          </Button>
        </div>
      )}

      <PriceChangedDialog
        changes={editPriceChanges}
        lines={editRepriceLines}
        submitting={saving}
        confirmLabel="Update order at new prices"
        confirmPendingLabel="Updating…"
        cancelLabel="Keep editing"
        onConfirm={() => {
          setEditPriceChanges(null)
          void handleSaveEdit(true)
        }}
        onBackToCart={() => setEditPriceChanges(null)}
      />
    </div>
  )
}
